import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  FunctionComponent,
  MutableRefObject,
  useEffect,
} from 'react'
import styled, { css } from 'styled-components'

import SwiperClass from 'swiper/types/swiper-class'

import contextViewport, { ViewportTypeValues } from '@contexts/viewport'
import { mobileCSS, tabletCSS, desktopCSS } from '@measures/responsive'
import { SwiperProps } from 'swiper/react'
import Swiper, { SwiperSlide, classNames } from '@components/Swiper'

import RecommendationsCard from './RecommendationsCard'
import { RecommendedItem } from '@hooks/useRecommendations'

type DeviceSwiperConfigType = Pick<
  SwiperProps,
  | 'navigation'
  | 'slidesPerView'
  | 'slidesPerGroup'
  | 'spaceBetween'
  | 'freeMode'
  | 'centeredSlides'
  | 'centeredSlidesBounds'
  | 'slidesOffsetBefore'
  | 'slidesOffsetAfter'
  | 'simulateTouch'
>

const SwiperWrapper = styled.div<
  Pick<
    RecommendationsSwiperProps,
    'smallSize' | 'swiperItemHeight' | 'swiperItemWidth'
  >
>`
  ${({ smallSize, swiperItemWidth, swiperItemHeight }) => {
    const mobileStyles = css`
      width: ${swiperItemWidth}px;
      height: ${swiperItemHeight}px;

      &:first-child {
        margin-left: 6px;
      }

      &:last-child {
        margin-right: 6px;
      }
    `

    const desktopStyles = smallSize
      ? mobileStyles
      : css`
          width: 416px;
          height: 234px;

          &:first-child {
            margin-left: 24px;
          }

          &:last-child {
            margin-right: 24px;
          }
        `

    const tabletStyles = smallSize
      ? mobileStyles
      : css`
          ${desktopStyles}
          width: ${swiperItemWidth}px;
          height: ${swiperItemHeight}px;
        `

    return css`
      & .${classNames.wrapper} {
        padding: 6px 10px 6px 10px;

        & .${classNames.slide} {
          ${mobileCSS(mobileStyles)}

          ${tabletCSS(tabletStyles)}

          ${desktopCSS(desktopStyles)}
        }
      }

      &
        .${classNames.navigationButtonPrev},
        &
        .${classNames.navigationButtonNext} {
        box-sizing: unset;
      }
    `
  }}
`

const commonConfig = {
  centeredSlides: false,
  centeredSlidesBounds: false,
  slidesPerView: 'auto' as SwiperProps['slidesPerView'],
}

const mobileConfig: DeviceSwiperConfigType = {
  ...commonConfig,
  freeMode: true,
  navigation: false,
  spaceBetween: 16,
}

const desktopConfig: DeviceSwiperConfigType = {
  ...commonConfig,
  simulateTouch: false,
  freeMode: false,
  navigation: true,
  spaceBetween: 16,
  slidesPerGroup: 1,
  slidesOffsetAfter: 16,
}

const { ViewportTypeContext } = contextViewport

export interface RecommendationsSwiperProps {
  activeItem?: RecommendedItem
  data: RecommendedItem[]
  swiperRef?: MutableRefObject<SwiperClass | null>
  smallSize?: boolean
  hidden?: boolean
  videoPlayingStatus?: string
  onItemClick: (id: string, recommendationsData: RecommendedItem) => void
  onItemImpression: (recommendationsData: RecommendedItem) => void
  onSliderMove: () => void
  onNavigationChange: () => void
  swiperItemHeight: number
  swiperItemWidth: number
}

const RecommendationsSwiper: FunctionComponent<RecommendationsSwiperProps> = ({
  activeItem,
  data,
  onItemClick,
  onItemImpression,
  onSliderMove,
  onNavigationChange,
  swiperRef: externalSwiperRef,
  smallSize,
  hidden,
  swiperItemHeight,
  swiperItemWidth,
}) => {
  const viewportType = useContext(ViewportTypeContext)
  const isDesktop = viewportType === ViewportTypeValues.DESKTOP

  const swiperConfig = isDesktop
    ? {
        ...desktopConfig,
        navigation: desktopConfig.navigation && data?.length > 1,
      }
    : mobileConfig

  const swiperRef = useRef<SwiperClass | null>(null)

  useEffect(() => {
    swiperRef.current?.update?.()
  }, [hidden])

  const formattedSlides = useMemo(
    () =>
      data?.map((recommendationsData: RecommendedItem) => {
        return {
          onClick: () =>
            onItemClick(recommendationsData?.articleId, recommendationsData),
          onImpression: () => onItemImpression(recommendationsData),
          ...recommendationsData,
        }
      }) || [],
    [data, onItemClick, onItemImpression]
  )

  const onInit = useCallback(
    (swiper: any) => {
      swiperRef.current = swiper

      if (externalSwiperRef) {
        externalSwiperRef.current = swiper
      }
    },
    [externalSwiperRef]
  )

  return (
    <SwiperWrapper
      smallSize={smallSize}
      swiperItemHeight={swiperItemHeight}
      swiperItemWidth={swiperItemWidth}>
      <Swiper
        {...swiperConfig}
        initialSlide={0}
        onInit={onInit}
        onSliderMove={onSliderMove}
        onNavigationChange={onNavigationChange}
        slides={formattedSlides?.map((item) => (
          <SwiperSlide key={item.articleId}>
            <RecommendationsCard
              {...item}
              isActive={activeItem && item.articleId === activeItem?.articleId}
              smallSize={smallSize}
              hidden={hidden}
            />
          </SwiperSlide>
        ))}
      />
    </SwiperWrapper>
  )
}

export default RecommendationsSwiper
